import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAsync, useToast } from '../../lib/store';
import type { WarAdminList, WarConfigResponse } from '../../lib/types';
import { Button, ErrorState, Panel } from '../ui';
import { formatNumber, timeAgo } from '../../lib/format';

/**
 * The Guild War console — design PART 50.
 *
 * The `enabled` switch is deliberately the first thing on the page and is
 * called out rather than left as one number among twenty: wars ship off, and
 * turning them on is a decision about opening a competitive season, not a
 * balance tweak. Everything else is the same clamped key/value console the
 * breeding and economy tabs use.
 */
export function AdminWarsTab() {
  return (
    <div className="stack">
      <WarsOverview />
      <WarConfigPanel />
    </div>
  );
}

function WarsOverview() {
  const { push } = useToast();
  const state = useAsync<WarAdminList>(() => api.get('/admin/wars/list'));
  const [busy, setBusy] = useState<string | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 180 }} />;

  const data = state.data;

  async function act(what: string, run: () => Promise<unknown>, message: string) {
    setBusy(what);
    try {
      await run();
      push('success', message);
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Panel title="⚔ Season" hint={data.seasonKey}>
        <div className="row row--between">
          <span className="tiny">
            Guild Wars are <strong>{data.enabled ? 'running' : 'switched off'}</strong>.
          </span>
          <span className="chip">{data.wars.length} wars on record</span>
        </div>
        {!data.enabled && (
          <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
            Set <code>enabled</code> to 1 below to open a season. The rollout plan ends at a
            balancing preseason, so run Season 0 before treating the ladder as competitive.
          </p>
        )}
        <div className="row" style={{ gap: 6, marginTop: 'var(--gap-sm)' }}>
          <Button
            size="sm"
            loading={busy === 'match'}
            onClick={() =>
              void act('match', () => api.post('/admin/wars/matchmake', {}), 'Matchmaking ran.')
            }
          >
            Run matchmaking now
          </Button>
          <Button
            size="sm"
            variant="ghost"
            loading={busy === 'season'}
            onClick={() =>
              void act('season', () => api.post('/admin/wars/season/roll', {}), 'Season rolled.')
            }
          >
            Roll the season
          </Button>
        </div>
      </Panel>

      <Panel title="◔ Queue" hint={`${data.queue.length} waiting`}>
        {data.queue.length === 0 ? (
          <p className="tiny muted">Nobody is waiting for a match.</p>
        ) : (
          <div className="stack-sm">
            {data.queue.map((row) => (
              <div key={row.guildId} className="row row--between tiny">
                <span>{row.guildName}</span>
                <span className="muted">
                  roster level ~{row.rosterLevel} · {row.eligible} eligible · {timeAgo(row.enteredAt)}
                </span>
              </div>
            ))}
            <p className="tiny muted">
              A guild stays queued when nobody is within its level band — that is correct, not a
              stuck queue. Widening <code>maxLevelGap</code> pairs them at the cost of one-sided
              wars.
            </p>
          </div>
        )}
      </Panel>

      <Panel title="▤ Recent wars">
        {data.wars.length === 0 ? (
          <p className="tiny muted">No wars yet.</p>
        ) : (
          <div className="stack-sm">
            {data.wars.map((war) => (
              <div key={war.id} className="row row--between tiny">
                <span>
                  <span className="chip chip--sm" style={{ marginRight: 6 }}>
                    {war.status}
                  </span>
                  {war.guildA} {formatNumber(war.scoreA)} — {formatNumber(war.scoreB)} {war.guildB}
                </span>
                <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <span className="muted">
                    {war.participants}p · {war.battles}b
                  </span>
                  {(war.status === 'PENDING' || war.status === 'ACTIVE') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={busy === war.id}
                      onClick={() =>
                        void act(
                          war.id,
                          () => api.post(`/admin/wars/${war.id}/cancel`, {}),
                          'War cancelled — no ratings moved.',
                        )
                      }
                    >
                      Cancel
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
          Cancelling never moves a rating, which is what makes it safe on a broken war.
        </p>
      </Panel>
    </>
  );
}

function WarConfigPanel() {
  const { push } = useToast();
  const state = useAsync<WarConfigResponse>(() => api.get('/admin/wars/config'));
  const [saving, setSaving] = useState<string | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 240 }} />;

  const groups = [...new Set(state.data.fields.map((field) => field.group))];

  async function save(key: string, value: string) {
    setSaving(key);
    try {
      const result = await api.patch<{ key: string; value: number }>('/admin/wars/config', {
        key,
        value: Number(value),
      });
      push('success', `${key} = ${result.value}`);
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save that.');
    } finally {
      setSaving(null);
    }
  }

  return (
    <>
      {groups.map((group) => (
        <Panel key={group} title={`⚙ ${group}`}>
          <div className="stack-sm">
            {state.data!.fields
              .filter((field) => field.group === group)
              .map((field) => (
                <div key={field.key} className="row row--between" style={{ gap: 'var(--gap-sm)' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong className="tiny">{field.label}</strong>
                    <div className="tiny muted">{field.help}</div>
                    {field.value !== field.default && (
                      <div className="tiny muted">default {field.default}</div>
                    )}
                  </span>
                  <input
                    // Keyed on the effective value so a clamped save shows what
                    // actually took effect rather than the rejected input.
                    key={`${field.key}:${field.value}`}
                    className="input"
                    type="number"
                    style={{ width: 110 }}
                    defaultValue={field.value}
                    disabled={saving === field.key}
                    onBlur={(event) => {
                      if (Number(event.target.value) !== field.value) {
                        void save(field.key, event.target.value);
                      }
                    }}
                  />
                </div>
              ))}
          </div>
        </Panel>
      ))}
      <p className="tiny muted">
        Keep <code>maxLevelGap</code> tight. The battle simulation measured a 10-level gap winning
        ~99% of battles, so widening it produces wars nobody can win — run{' '}
        <code>npm run sim:wars -w server</code> after changing it.
      </p>
    </>
  );
}
